import type React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Spinner, makeStyles, tokens } from '@fluentui/react-components';
import { useAuth } from 'hooks/useAuth';
import { useStatusPanelMode } from 'contexts/StatusPanelContext';
import { useWallAccess } from 'hooks/useWallAccess';
import type { AuthRole } from 'types/auth';

const useStyles = makeStyles({
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100vh',
    backgroundColor: tokens.colorNeutralBackground1,
  },
});

interface ProtectedRouteProps {
  children: React.ReactNode;
  /** Required roles - user must have at least one */
  requiredRoles?: AuthRole[];
  /** Allow unauthenticated access (for wall-screen mode with IP whitelist) */
  allowPublic?: boolean;
  /** Skip app access check (for routes that don't require security group membership) */
  skipAppAccessCheck?: boolean;
}

/**
 * Route wrapper that enforces authentication and app access.
 * Redirects to login if user is not authenticated.
 * Redirects to unauthorized if user is not in required security groups.
 * Supports role-based access control.
 */
export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredRoles,
  allowPublic = false,
  skipAppAccessCheck = false,
}) => {
  const styles = useStyles();
  const location = useLocation();
  const { isLoading, isAuthenticated, hasAnyRole, hasAppAccess } = useAuth();
  const { isWallScreen } = useStatusPanelMode();
  const { isAllowed: wallAccessAllowed, isLoading: wallAccessLoading } = useWallAccess(allowPublic && isWallScreen);

  // Wall-screen mode: check if IP is whitelisted via backend
  if (allowPublic && isWallScreen) {
    // Still loading wall access check
    if (wallAccessLoading) {
      return (
        <div className={styles.loadingContainer}>
          <Spinner size="large" label="Checking access..." />
        </div>
      );
    }

    // Wall access allowed (IP is whitelisted)
    if (wallAccessAllowed) {
      return <>{children}</>;
    }

    // Wall access not allowed - fall through to normal auth flow
    // If not authenticated, will redirect to login
  }

  // Show loading spinner while auth state is being determined
  if (isLoading) {
    return (
      <div className={styles.loadingContainer}>
        <Spinner size="large" label="Loading..." />
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    // Preserve the attempted URL for redirect after login
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check app access - user must be member of soc-operators-asg or soc-admin-asg
  if (!skipAppAccessCheck && !hasAppAccess) {
    return <Navigate to="/unauthorized" replace />;
  }

  // Check role-based access
  if (requiredRoles && requiredRoles.length > 0 && !hasAnyRole(requiredRoles)) {
    return <Navigate to="/unauthorized" replace />;
  }

  return <>{children}</>;
};
